import React, { useEffect, useId, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  Select,
  Textarea,
} from '../../../components/ui';
import { listTaxonomies, type TaxonomyItem } from '../../../services/taxonomiesService';
import { stakeholderService } from '../../../services/stakeholderService';
import { organizationService } from '../../../services/organizationService';
import type { ConnectionLead, EntityType } from '../types';
import { EntityAvatar } from './ui';

/*
 * Adding a counterpart the research turned up, without leaving the canvas.
 *
 * The research already established who this is — a title, an employer, a
 * country, the sources it read. Sending the analyst to an empty form on
 * another page threw all of that away and asked them to type it again from
 * memory. Everything known arrives filled in; the analyst's job is to correct
 * it and confirm, and the sources sit right there to check it against.
 */

const errorOf = (e: any, fallback: string) => {
  const m = e?.response?.data?.message;
  return Array.isArray(m) ? m.join(', ') : m || e?.message || fallback;
};

/**
 * Last word is the surname, the rest is the given name — the same convention
 * the government map uses when it turns an official's name into a record.
 * Wrong for some names, which is why it lands in an editable field.
 */
export function splitName(full: string): { firstName: string; lastName: string } {
  const words = (full || '').replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
  if (words.length === 0) return { firstName: '', lastName: '' };
  if (words.length === 1) return { firstName: words[0], lastName: words[0] };
  return { firstName: words.slice(0, -1).join(' '), lastName: words[words.length - 1] };
}

/** A taxonomy entry whose label the research's own words point at, if any */
export function guessTaxonomy(hint: string | null | undefined, items: TaxonomyItem[]): string {
  if (!hint) return '';
  const text = hint.toLowerCase();
  const hit = items.find((item) => text.includes(item.label.toLowerCase()));
  return hit ? hit.key || hit.label : '';
}

/** What the research knows about this counterpart, as a person's record */
function personDraft(lead: ConnectionLead | null) {
  const profile = lead?.targetProfile ?? null;
  const { firstName, lastName } = splitName(lead?.targetLabel ?? '');
  const standing = [profile?.role, profile?.organization].filter(Boolean).join(', ');
  return {
    firstName,
    lastName,
    category: '',
    location: profile?.country ?? '',
    // The one-line "who they are" first, then their standing, then why they
    // came up at all — the analyst can cut what they do not want kept
    bio: [profile?.about, standing, lead?.summary].filter(Boolean).join('\n\n'),
    linkedinUrl: profile?.website && /linkedin\./i.test(profile.website) ? profile.website : '',
  };
}

/** The same, as an organisation's record */
function bodyDraft(lead: ConnectionLead | null) {
  const profile = lead?.targetProfile ?? null;
  return {
    name: lead?.targetLabel ?? '',
    type: '',
    description: [profile?.about, lead?.summary].filter(Boolean).join('\n\n'),
    address: profile?.country ?? '',
    website: profile?.website && !/linkedin\./i.test(profile.website) ? profile.website : '',
    aliases: (profile?.aliases ?? []).join(', '),
    parentName: profile?.organization ?? '',
  };
}

export const AddToDirectoryDialog: React.FC<{
  /** The lead whose counterpart is missing; null keeps the dialog shut */
  lead: ConnectionLead | null;
  onClose: () => void;
  /** The record now exists; `accepted` says whether the tie was recorded too */
  onCreated: (created: { id: string; type: EntityType; label: string; accepted: boolean }) => void;
  /** Records the lead's relationship once the counterpart exists */
  onAccept: (lead: ConnectionLead) => Promise<void>;
}> = ({ lead, onClose, onCreated, onAccept }) => {
  const titleId = useId();
  const ids = useId();
  const open = !!lead;
  const isPerson = lead?.targetType !== 'organization';

  const [categories, setCategories] = useState<TaxonomyItem[]>([]);
  const [person, setPerson] = useState(() => personDraft(lead));
  const [body, setBody] = useState(() => bodyDraft(lead));
  const [alsoAccept, setAlsoAccept] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Everything the research found, poured into the form each time it opens
  useEffect(() => {
    if (!lead) return;
    setPerson(personDraft(lead));
    setBody(bodyDraft(lead));
    setAlsoAccept(true);
    setError(null);
  }, [lead]);

  useEffect(() => {
    if (!open) return;
    let dropped = false;
    listTaxonomies(isPerson ? 'stakeholder_category' : 'organization_type')
      .then((items) => !dropped && setCategories(items))
      .catch(() => undefined);
    return () => {
      dropped = true;
    };
  }, [open, isPerson]);

  // The model's own words often name the kind of thing this is
  const guessed = useMemo(() => {
    if (!lead || !categories.length) return '';
    const hint = isPerson
      ? [lead.targetProfile?.role, lead.targetProfile?.about].filter(Boolean).join(' ')
      : [lead.targetProfile?.role, lead.targetProfile?.about, lead.targetLabel].filter(Boolean).join(' ');
    return guessTaxonomy(hint, categories);
  }, [lead, categories, isPerson]);

  useEffect(() => {
    if (!guessed) return;
    if (isPerson) setPerson((draft) => (draft.category ? draft : { ...draft, category: guessed }));
    else setBody((draft) => (draft.type ? draft : { ...draft, type: guessed }));
  }, [guessed, isPerson]);

  if (!lead) return null;

  const known = lead.targetProfile;
  const ready = isPerson
    ? !!person.firstName.trim() && !!person.lastName.trim() && !!person.category
    : !!body.name.trim() && !!body.type;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      let created: { id: string; label: string };
      if (isPerson) {
        const saved = await stakeholderService.createStakeholder({
          firstName: person.firstName.trim(),
          lastName: person.lastName.trim(),
          category: person.category,
          location: person.location.trim() || undefined,
          bio: person.bio.trim() || undefined,
          linkedinUrl: person.linkedinUrl.trim() || undefined,
        });
        created = { id: saved.id, label: `${saved.firstName} ${saved.lastName}`.trim() };
      } else {
        const saved = await organizationService.createOrganization({
          name: body.name.trim(),
          type: body.type,
          description: body.description.trim() || undefined,
          address: body.address.trim() || undefined,
          website: body.website.trim() || undefined,
          aliases: body.aliases
            .split(',')
            .map((alias) => alias.trim())
            .filter(Boolean),
        });
        created = { id: saved.id, label: saved.name };
      }

      // Accepting re-matches the lead by name, so the tie lands on the record
      // that has just been made
      let accepted = false;
      if (alsoAccept) {
        try {
          await onAccept(lead);
          accepted = true;
        } catch {
          // The record is real either way; the lead can be accepted by hand
        }
      }
      onCreated({ ...created, type: lead.targetType, accepted });
    } catch (e) {
      setError(errorOf(e, 'That record could not be created'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} labelledBy={titleId} handle className="max-h-[92dvh] sm:max-w-lg">
      <DialogHeader
        id={titleId}
        title={isPerson ? 'Add this person' : 'Add this organisation'}
        eyebrow="From a connection lead"
        onClose={onClose}
      />
      <DialogBody className="space-y-3">
        <div className="flex items-center gap-2 rounded-xl bg-neutral-50 px-3 py-2.5">
          <EntityAvatar
            node={{ id: 'new', type: lead.targetType, label: lead.targetLabel, status: '' }}
            size="sm"
          />
          <div className="min-w-0">
            <p className="truncate text-[13px] text-ink">{lead.targetLabel}</p>
            <p className="truncate text-[12px] text-neutral-500">
              {lead.label} {lead.subjectLabel}
              {lead.timeframe ? ` · ${lead.timeframe}` : ''}
            </p>
          </div>
        </div>

        {isPerson ? (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="First name" htmlFor={`${ids}-first`} required>
                <Input
                  id={`${ids}-first`}
                  value={person.firstName}
                  onChange={(e) => setPerson({ ...person, firstName: e.target.value })}
                />
              </Field>
              <Field label="Last name" htmlFor={`${ids}-last`} required>
                <Input
                  id={`${ids}-last`}
                  value={person.lastName}
                  onChange={(e) => setPerson({ ...person, lastName: e.target.value })}
                />
              </Field>
            </div>
            <Field label="Category" htmlFor={`${ids}-cat`} required>
              <Select
                id={`${ids}-cat`}
                value={person.category}
                onChange={(e) => setPerson({ ...person, category: e.target.value })}
              >
                <option value="">Choose one</option>
                {categories.map((item) => (
                  <option key={item.id} value={item.key || item.label}>
                    {item.label}
                  </option>
                ))}
              </Select>
            </Field>
            {known?.role && (
              <Field label="Position found" hint="Recorded in the notes; add the role properly from their profile">
                <Input value={[known.role, known.organization].filter(Boolean).join(' · ')} readOnly disabled />
              </Field>
            )}
            <Field label="Location" htmlFor={`${ids}-loc`}>
              <Input
                id={`${ids}-loc`}
                value={person.location}
                onChange={(e) => setPerson({ ...person, location: e.target.value })}
                placeholder="Country or city"
              />
            </Field>
            <Field label="Notes" htmlFor={`${ids}-bio`}>
              <Textarea
                id={`${ids}-bio`}
                rows={4}
                value={person.bio}
                onChange={(e) => setPerson({ ...person, bio: e.target.value })}
              />
            </Field>
          </>
        ) : (
          <>
            <Field label="Name" htmlFor={`${ids}-name`} required>
              <Input id={`${ids}-name`} value={body.name} onChange={(e) => setBody({ ...body, name: e.target.value })} />
            </Field>
            <Field label="Type" htmlFor={`${ids}-type`} required>
              <Select id={`${ids}-type`} value={body.type} onChange={(e) => setBody({ ...body, type: e.target.value })}>
                <option value="">Choose one</option>
                {categories.map((item) => (
                  <option key={item.id} value={item.key || item.label}>
                    {item.label}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Country" htmlFor={`${ids}-country`}>
                <Input
                  id={`${ids}-country`}
                  value={body.address}
                  onChange={(e) => setBody({ ...body, address: e.target.value })}
                />
              </Field>
              <Field label="Website" htmlFor={`${ids}-site`}>
                <Input
                  id={`${ids}-site`}
                  value={body.website}
                  onChange={(e) => setBody({ ...body, website: e.target.value })}
                  placeholder="https://"
                />
              </Field>
            </div>
            <Field label="Also known as" htmlFor={`${ids}-aliases`} hint="Separated by commas">
              <Input
                id={`${ids}-aliases`}
                value={body.aliases}
                onChange={(e) => setBody({ ...body, aliases: e.target.value })}
              />
            </Field>
            {body.parentName && (
              <Field label="Part of" hint="Set the parent on the record once it exists">
                <Input value={body.parentName} readOnly disabled />
              </Field>
            )}
            <Field label="Description" htmlFor={`${ids}-desc`}>
              <Textarea
                id={`${ids}-desc`}
                rows={4}
                value={body.description}
                onChange={(e) => setBody({ ...body, description: e.target.value })}
              />
            </Field>
          </>
        )}

        {lead.evidence.length > 0 && (
          <div className="rounded-xl border border-neutral-200">
            <p className="px-3 pb-1 pt-2.5 text-[11px] uppercase tracking-[0.08em] text-neutral-400">
              What this is based on
            </p>
            <ul className="divide-y divide-neutral-200/70">
              {lead.evidence.slice(0, 4).map((item, index) => (
                <li key={index} className="px-3 py-2">
                  <p className="text-[12.5px] leading-snug text-neutral-700">{item.claim}</p>
                  {item.url && (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="mt-0.5 inline-flex items-center gap-1 text-[12px] text-neutral-500 hover:text-ink"
                    >
                      {item.source || new URL(item.url).hostname}
                      <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <Checkbox
          checked={alsoAccept}
          onChange={(e) => setAlsoAccept(e.target.checked)}
          label={`Record the connection to ${lead.subjectLabel} as well`}
        />

        {error && <p className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">{error}</p>}
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" loading={saving} disabled={!ready} onClick={submit}>
          Add to directory
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
